import React, { useState, useEffect } from 'react';
import { 
  Send, Loader, CheckCircle, XCircle, Clock, Eye, 
  ArrowRight, ArrowLeft, Users, FileText, EyeOff, 
  DollarSign, AlertCircle, RefreshCw 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { metaApiService } from '../../services/metaApiService';
import { 
  WhatsAppTemplate, 
  MetaApiConfig, 
  FilterType, 
  DisparoFilter, 
  DisparoDestinatario, 
  DisparoPreview, 
  DisparoStatus,
  ClienteComMensalidadeVencida
} from '../../types/FireAgent';
import { useNotification } from '../../hooks/useNotification';
import { User } from '../../types/User';
import { metaTemplateService } from '../../services/metaTemplateService';

type DisparoStep = 'template' | 'filtro' | 'preview' | 'confirmacao' | 'acompanhamento';

const DisparoMensagens: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showInfo, NotificationComponent } = useNotification();
  
  // Estados gerais
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<DisparoStep>('template');
  const [metaApiConfig, setMetaApiConfig] = useState<MetaApiConfig | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [carregandoTemplates, setCarregandoTemplates] = useState(false);
  
  // Etapa 1: Seleção de Template
  const [templateSelecionado, setTemplateSelecionado] = useState<WhatsAppTemplate | null>(null);
  
  // Etapa 2: Seleção de Filtro e Destinatários
  const [filterType, setFilterType] = useState<FilterType>('inativos');
  const [diasAtraso, setDiasAtraso] = useState(1);
  const [destinatarios, setDestinatarios] = useState<DisparoDestinatario[]>([]);
  const [destinatariosSelecionados, setDestinatariosSelecionados] = useState<Set<string>>(new Set());
  const [carregandoDestinatarios, setCarregandoDestinatarios] = useState(false);
  const [todosAlunos, setTodosAlunos] = useState<User[]>([]);
  
  // Etapa 3: Preview
  const [preview, setPreview] = useState<DisparoPreview | null>(null);
  
  // Etapa 4: Confirmação
  const [custoTotal, setCustoTotal] = useState(0);
  
  // Etapa 5: Acompanhamento
  const [disparoStatus, setDisparoStatus] = useState<Map<string, DisparoStatus>>(new Map());
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState({ total: 0, enviados: 0, entregues: 0, lidos: 0, falhas: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      
      // Carregar configuração Meta API
      const savedMetaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (savedMetaApiConfig) {
        setMetaApiConfig(savedMetaApiConfig);
        metaApiService.setConfig(savedMetaApiConfig);
        
        console.log('📋 Configuração Meta API carregada:', {
          hasAccessToken: !!savedMetaApiConfig.accessToken,
          hasBusinessAccountId: !!savedMetaApiConfig.businessAccountId,
          hasPhoneNumberId: !!savedMetaApiConfig.phoneNumberId
        });
        
        if (savedMetaApiConfig.accessToken && savedMetaApiConfig.businessAccountId) {
          // Aguardar um pouco para garantir que o estado foi atualizado
          setTimeout(async () => {
            await loadTemplates();
          }, 100);
        } else {
          console.warn('⚠️ Configuração incompleta - faltando accessToken ou businessAccountId');
        }
      } else {
        showInfo('Configuração Necessária', 'Configure o Meta API em Configurações antes de usar o disparo de mensagens.');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showError('Erro', 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    // Usar o estado atual ou buscar novamente se necessário
    let configParaUsar = metaApiConfig;
    
    // Se não tiver config no estado, buscar novamente
    if (!configParaUsar && userProfile?.empresaCnpj) {
      try {
        configParaUsar = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
        if (configParaUsar) {
          setMetaApiConfig(configParaUsar);
        }
      } catch (error) {
        console.error('Erro ao buscar configuração:', error);
      }
    }
    
    if (!configParaUsar?.accessToken || !configParaUsar?.businessAccountId) {
      console.warn('⚠️ Meta API não configurado para carregar templates', {
        hasConfig: !!configParaUsar,
        hasAccessToken: !!configParaUsar?.accessToken,
        hasBusinessAccountId: !!configParaUsar?.businessAccountId
      });
      return;
    }

    try {
      setCarregandoTemplates(true);
      
      // Garantir que o serviço está configurado
      metaApiService.setConfig(configParaUsar);
      
      console.log('🔍 Carregando templates via Meta API...', {
        businessAccountId: configParaUsar.businessAccountId,
        hasAccessToken: !!configParaUsar.accessToken
      });
      
      // Tentar listar todos os templates primeiro para debug
      const allTemplates = await metaApiService.listAllTemplates();
      console.log('📋 Todos os templates (incluindo pendentes):', allTemplates);
      console.log('📊 Status dos templates:', allTemplates.map(t => ({ name: t.name, status: t.status })));
      
      // Filtrar apenas aprovados
      const templatesList = await metaApiService.listWhatsAppTemplates();
      
      console.log('✅ Templates aprovados retornados:', templatesList);
      console.log('📊 Total de templates aprovados:', templatesList.length);
      
      // Se não encontrou aprovados mas há templates, mostrar aviso
      if (templatesList.length === 0 && allTemplates.length > 0) {
        const statusCounts = allTemplates.reduce((acc, t) => {
          acc[t.status] = (acc[t.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.warn('⚠️ Nenhum template aprovado encontrado. Status dos templates:', statusCounts);
        showInfo(
          'Templates Pendentes',
          `Encontrados ${allTemplates.length} template(s), mas nenhum está aprovado.\n\n` +
          `Status: ${JSON.stringify(statusCounts, null, 2)}\n\n` +
          `Aguarde a aprovação no Meta Business Manager ou verifique se o template está realmente aprovado.`
        );
      } else if (templatesList.length === 0) {
        console.warn('⚠️ Nenhum template encontrado. Verifique se há templates criados no Meta Business Manager.');
      }
      
      setTemplates(templatesList);
    } catch (error: any) {
      console.error('❌ Erro ao carregar templates:', error);
      const errorMessage = error.message || 'Erro desconhecido ao carregar templates';
      
      if (errorMessage.includes('Business Account ID') || errorMessage.includes('WABA ID')) {
        showError(
          'Business Account ID Necessário',
          `${errorMessage}\n\n` +
          `Como encontrar o Business Account ID:\n` +
          `1. Acesse: https://business.facebook.com\n` +
          `2. Vá em: Configurações > Contas do WhatsApp\n` +
          `3. Selecione sua conta do WhatsApp Business\n` +
          `4. O Business Account ID aparece no topo da página\n` +
          `5. Configure nas: Configurações > Integração > Meta API`
        );
      } else {
        showError('Erro ao Carregar Templates', errorMessage);
      }
      
      setTemplates([]);
    } finally {
      setCarregandoTemplates(false);
    }
  };

  const loadDestinatarios = async (tipoFiltro?: FilterType) => {
    if (!userProfile?.empresaCnpj) return;

    const tipoParaUsar = tipoFiltro || filterType;

    try {
      setCarregandoDestinatarios(true);
      let destinatariosData: DisparoDestinatario[] = [];

      if (tipoParaUsar === 'inativos') {
        // Carregar alunos inativos
        const alunos = await firebaseService.getAllAlunos(userProfile.empresaCnpj);
        const alunosInativos = alunos.filter(a => !a.bAtivo && (a.cellular || a.telefone));
        
        destinatariosData = alunosInativos.map(aluno => ({
          alunoId: aluno.id,
          alunoNome: aluno.nome || 'Sem nome',
          alunoTelefone: aluno.cellular || aluno.telefone || '',
          alunoEmail: aluno.email
        }));
      } else if (tipoParaUsar === 'atraso') {
        // Carregar clientes em atraso
        const clientesVencidos = await firebaseService.getMensalidadesVencidas(
          userProfile.empresaCnpj, 
          diasAtraso
        );
        
        destinatariosData = clientesVencidos.map(cliente => ({
          alunoId: cliente.alunoId,
          alunoNome: cliente.alunoNome,
          alunoTelefone: cliente.alunoTelefone,
          alunoEmail: cliente.alunoEmail,
          mensalidadeId: cliente.mensalidadeId,
          valor: cliente.valor,
          dataVencimento: cliente.dataVencimento,
          diasAtraso: cliente.diasAtraso,
          status: cliente.status
        }));
      } else if (tipoParaUsar === 'manual') {
        // Carregar todos os alunos para seleção manual
        const alunos = await firebaseService.getAllAlunos(userProfile.empresaCnpj);
        setTodosAlunos(alunos);
        
        destinatariosData = alunos
          .filter(a => a.cellular || a.telefone)
          .map(aluno => ({
            alunoId: aluno.id,
            alunoNome: aluno.nome || 'Sem nome',
            alunoTelefone: aluno.cellular || aluno.telefone || '',
            alunoEmail: aluno.email
          }));
      }

      setDestinatarios(destinatariosData);
      
      // Selecionar todos por padrão
      const allIds = new Set(destinatariosData.map(d => d.alunoId));
      setDestinatariosSelecionados(allIds);
    } catch (error: any) {
      console.error('Erro ao carregar destinatários:', error);
      showError('Erro', `Erro ao carregar destinatários: ${error.message}`);
    } finally {
      setCarregandoDestinatarios(false);
    }
  };

  const calcularCusto = (quantidade: number): number => {
    // Custo base da Meta API: aproximadamente R$ 0,10 por mensagem (pode variar)
    // Este valor deve ser configurável ou obtido da API
    const custoPorMensagem = 0.10;
    return quantidade * custoPorMensagem;
  };

  const gerarPreview = () => {
    if (!templateSelecionado || destinatarios.length === 0) return;

    // Pegar o primeiro destinatário para preview
    const primeiroDestinatario = destinatarios[0];
    
    // Montar mensagem preview (lógica similar à que já existe)
    let mensagemPreview = '';
    
    if (templateSelecionado.components) {
      templateSelecionado.components.forEach((component: any) => {
        if (component.type === 'HEADER' && component.text) {
          let headerText = component.text;
          // Substituir variáveis se houver
          headerText = headerText.replace(/\{\{1\}\}/g, primeiroDestinatario.alunoNome);
          mensagemPreview += headerText + '\n\n';
        } else if (component.type === 'BODY' && component.text) {
          let bodyText = component.text;
          // Substituir variáveis
          bodyText = bodyText.replace(/\{\{1\}\}/g, primeiroDestinatario.alunoNome);
          if (primeiroDestinatario.valor) {
            bodyText = bodyText.replace(/\{\{2\}\}/g, `R$ ${primeiroDestinatario.valor.toFixed(2).replace('.', ',')}`);
          }
          if (primeiroDestinatario.diasAtraso) {
            bodyText = bodyText.replace(/\{\{3\}\}/g, primeiroDestinatario.diasAtraso.toString());
          }
          if (primeiroDestinatario.dataVencimento) {
            const data = new Date(primeiroDestinatario.dataVencimento);
            bodyText = bodyText.replace(/\{\{4\}\}/g, data.toLocaleDateString('pt-BR'));
          }
          mensagemPreview += bodyText;
        } else if (component.type === 'FOOTER' && component.text) {
          mensagemPreview += '\n\n' + component.text;
        }
      });
    }

    const quantidade = destinatariosSelecionados.size;
    const custo = calcularCusto(quantidade);

    setPreview({
      template: templateSelecionado,
      destinatario: primeiroDestinatario,
      mensagemPreview: mensagemPreview.trim(),
      custoEstimado: custo
    });

    setCustoTotal(custo);
  };

  const handleNextStep = () => {
    if (currentStep === 'template' && templateSelecionado) {
      setCurrentStep('filtro');
      loadDestinatarios();
    } else if (currentStep === 'filtro' && destinatariosSelecionados.size > 0) {
      gerarPreview();
      setCurrentStep('preview');
    } else if (currentStep === 'preview') {
      setCurrentStep('confirmacao');
    } else if (currentStep === 'confirmacao') {
      setCurrentStep('acompanhamento');
      iniciarDisparo();
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'filtro') {
      setCurrentStep('template');
    } else if (currentStep === 'preview') {
      setCurrentStep('filtro');
    } else if (currentStep === 'confirmacao') {
      setCurrentStep('preview');
    }
  };

  const iniciarDisparo = async () => {
    if (!templateSelecionado || !metaApiConfig) return;

    setEnviando(true);
    const destinatariosParaEnviar = destinatarios.filter(d => 
      destinatariosSelecionados.has(d.alunoId)
    );

    setProgresso({
      total: destinatariosParaEnviar.length,
      enviados: 0,
      entregues: 0,
      lidos: 0,
      falhas: 0
    });

    // Inicializar status
    const statusMap = new Map<string, DisparoStatus>();
    destinatariosParaEnviar.forEach(dest => {
      statusMap.set(dest.alunoId, {
        alunoId: dest.alunoId,
        alunoNome: dest.alunoNome,
        alunoTelefone: dest.alunoTelefone,
        status: 'pending'
      });
    });
    setDisparoStatus(statusMap);

    // Buscar detalhes completos do template para análise precisa
    let templateDetails = templateSelecionado;
    try {
      if (templateSelecionado.id && metaApiConfig) {
        metaTemplateService.setConfig(metaApiConfig);
        const detailed = await metaTemplateService.getTemplateDetails(templateSelecionado.id);
        templateDetails = detailed;
        console.log('📋 Detalhes completos do template:', {
          name: detailed.name,
          components: detailed.components?.map((c: any) => ({
            type: c.type,
            text: c.text,
            hasVariables: c.text?.includes('{{')
          }))
        });
      }
    } catch (error) {
      console.warn('⚠️ Não foi possível buscar detalhes do template, usando dados disponíveis:', error);
    }

    // Enviar mensagens em sequência
    for (const destinatario of destinatariosParaEnviar) {
      try {
        const status = statusMap.get(destinatario.alunoId);
        if (status) {
          status.status = 'sending';
          status.timestamp = new Date().toISOString();
          setDisparoStatus(new Map(statusMap));
        }

        // Analisar o template para determinar quantos parâmetros são necessários
        let headerParamsCount = 0;
        let bodyParamsCount = 0;
        let headerHasMedia = false;
        let hasButtons = false;
        
        if (templateDetails.components) {
          templateDetails.components.forEach((comp: any) => {
            // Ignorar componentes BUTTONS - eles não recebem parâmetros
            if (comp.type === 'BUTTONS') {
              hasButtons = true;
              return;
            }
            
            if (comp.type === 'HEADER') {
              // Verificar se HEADER tem mídia (imagem, vídeo, documento, localização)
              if (comp.format && comp.format !== 'TEXT') {
                headerHasMedia = true;
                // HEADER com mídia não precisa de parâmetros de texto
                return;
              }
              
              // HEADER com texto - contar variáveis ({{1}}, {{text}}, etc.)
              if (comp.text) {
                const headerVars = comp.text.match(/\{\{[^}]+\}\}/g) || [];
                if (headerVars.length > 0) {
                  // Contar variáveis únicas na ordem que aparecem
                  const uniqueVars = new Set(headerVars);
                  headerParamsCount = uniqueVars.size;
                  console.log(`📝 HEADER tem ${headerParamsCount} variável(is):`, Array.from(uniqueVars));
                }
              }
            }
            
            if (comp.type === 'BODY' && comp.text) {
              // Contar todas as variáveis no body ({{1}}, {{2}}, {{text}}, {{amount}}, {{date}}, etc.)
              // A Meta API usa positional parameters, então contamos todas as variáveis na ordem que aparecem
              const bodyVars = comp.text.match(/\{\{[^}]+\}\}/g) || [];
              if (bodyVars.length > 0) {
                // Contar variáveis únicas na ordem que aparecem
                // Para templates com {{text}}, {{amount}}, {{date}}, contamos como 3 variáveis
                // IMPORTANTE: Para o template "clientes_inativos", mesmo que tenha {{1}} no HEADER e {{1}} no BODY,
                // são 2 parâmetros diferentes (um para cada componente)
                const uniqueVars = new Set(bodyVars);
                bodyParamsCount = uniqueVars.size;
                
                console.log(`📝 BODY tem ${bodyParamsCount} variável(is):`, Array.from(uniqueVars));
              }
            }
          });
        }
        
        console.log(`📊 Análise do template "${templateSelecionado.name}":`, {
          headerParams: headerParamsCount,
          bodyParams: bodyParamsCount,
          headerHasMedia,
          hasButtons,
          components: templateDetails.components?.map((c: any) => ({
            type: c.type,
            format: c.format,
            hasText: !!c.text,
            textPreview: c.text?.substring(0, 100),
            hasButtons: !!c.buttons,
            buttonCount: c.buttons?.length || 0
          }))
        });
        
        // Preparar parâmetros baseado na análise precisa do template
        const parametros: string[] = [];
        const nomeUsuario = userProfile?.nome || 'Equipe';
        
        // Para o template "clientes_inativos", construir parâmetros de forma simples e direta
        // Igual à página de configurações: HEADER {{1}} = nome do aluno, BODY {{1}} = nome do usuário logado
        if (templateSelecionado.name === 'clientes_inativos') {
          // Template clientes_inativos tem 2 parâmetros:
          // 1. HEADER {{1}} = nome do aluno
          // 2. BODY {{1}} = nome do usuário logado
          parametros.push(destinatario.alunoNome || 'Cliente');
          parametros.push(nomeUsuario);
        } else {
          // Para outros templates, usar a lógica normal
          // Primeiro, preencher parâmetros do HEADER (se houver e não for mídia)
          if (!headerHasMedia && headerParamsCount > 0) {
            for (let i = 0; i < headerParamsCount; i++) {
              parametros.push(destinatario.alunoNome);
            }
          }
          
          // Depois, preencher parâmetros do BODY na ordem correta
          // A ordem dos parâmetros segue a ordem das variáveis no template
          // Exemplo: {{text}}, {{amount}}, {{date}} → [nome, valor, data]
          // IMPORTANTE: Sempre preencher TODOS os parâmetros necessários, mesmo que alguns dados não estejam disponíveis
          if (bodyParamsCount > 0) {
            for (let i = 0; i < bodyParamsCount; i++) {
              // Primeira variável geralmente é nome
              if (i === 0) {
                parametros.push(destinatario.alunoNome || 'Cliente');
              } 
              // Segunda variável geralmente é valor/amount
              else if (i === 1) {
                if (destinatario.valor) {
                  parametros.push(destinatario.valor.toFixed(2).replace('.', ','));
                } else {
                  parametros.push('0,00'); // Valor padrão se não disponível
                }
              } 
              // Terceira variável geralmente é data/date
              else if (i === 2) {
                if (destinatario.dataVencimento) {
                  const data = new Date(destinatario.dataVencimento);
                  parametros.push(data.toLocaleDateString('pt-BR'));
                } else if (destinatario.diasAtraso !== undefined) {
                  // Fallback: usar dias de atraso se não tiver data
                  parametros.push(destinatario.diasAtraso.toString() + ' dias');
                } else {
                  parametros.push(new Date().toLocaleDateString('pt-BR')); // Data atual como fallback
                }
              } 
              // Quarta variável pode ser dias de atraso ou outros dados
              else if (i === 3) {
                if (destinatario.diasAtraso !== undefined) {
                  parametros.push(destinatario.diasAtraso.toString());
                } else {
                  parametros.push('0'); // Fallback
                }
              } 
              // Fallback para outras variáveis
              else {
                parametros.push('N/A');
              }
            }
          }
        }
        
        console.log(`📤 Enviando template "${templateSelecionado.name}":`, {
          totalParams: parametros.length,
          headerParams: headerParamsCount,
          bodyParams: bodyParamsCount,
          headerHasMedia,
          params: parametros,
          hasButtons
        });
        
        // Se o template não precisa de parâmetros, não enviar array vazio
        if (parametros.length === 0) {
          console.log('ℹ️ Template não requer parâmetros - será enviado sem components');
        }

        // Só enviar parâmetros se o template realmente precisa deles
        const response = await metaApiService.sendTemplateMessage(
          destinatario.alunoTelefone,
          templateSelecionado.name,
          parametros.length > 0 ? parametros : [], // Enviar array vazio se não houver parâmetros
          templateSelecionado.language
        );

        if (response.messages && response.messages[0]) {
          const messageId = response.messages[0].id;
          const status = statusMap.get(destinatario.alunoId);
          if (status) {
            status.status = 'sent';
            status.messageId = messageId;
            status.cost = calcularCusto(1);
            setProgresso(prev => ({ ...prev, enviados: prev.enviados + 1 }));
          }
        }

        setDisparoStatus(new Map(statusMap));
        
        // Aguardar um pouco entre envios para evitar rate limit
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error: any) {
        const status = statusMap.get(destinatario.alunoId);
        if (status) {
          status.status = 'failed';
          status.error = error.message;
        }
        setDisparoStatus(new Map(statusMap));
        setProgresso(prev => ({ ...prev, falhas: prev.falhas + 1 }));
      }
    }

    setEnviando(false);
    showSuccess('Disparo Concluído', `Mensagens enviadas: ${progresso.enviados}/${progresso.total}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!metaApiConfig) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Configuração Necessária</h3>
        <p className="text-gray-600 mb-4">
          Configure o Meta API em <strong>Integração → Configurações</strong> antes de usar o disparo de mensagens.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Disparo de Mensagens</h2>
        
        {/* Indicador de Etapas */}
        <div className="flex items-center justify-between mb-8">
          {[
            { key: 'template', label: 'Template', icon: FileText },
            { key: 'filtro', label: 'Destinatários', icon: Users },
            { key: 'preview', label: 'Preview', icon: Eye },
            { key: 'confirmacao', label: 'Confirmação', icon: CheckCircle },
            { key: 'acompanhamento', label: 'Acompanhamento', icon: RefreshCw }
          ].map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.key;
            const isCompleted = ['template', 'filtro', 'preview', 'confirmacao', 'acompanhamento'].indexOf(currentStep) > 
                                ['template', 'filtro', 'preview', 'confirmacao', 'acompanhamento'].indexOf(step.key);
            
            return (
              <React.Fragment key={step.key}>
                <div className={`flex flex-col items-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                    isActive ? 'border-blue-600 bg-blue-50' : 
                    isCompleted ? 'border-green-600 bg-green-50' : 
                    'border-gray-300 bg-gray-50'
                  }`}>
                    <StepIcon className="h-6 w-6" />
                  </div>
                  <span className="mt-2 text-sm font-medium">{step.label}</span>
                </div>
                {index < 4 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    isCompleted ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Conteúdo das Etapas */}
        <div className="border-t pt-6">
          {/* ETAPA 1: Seleção de Template */}
          {currentStep === 'template' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione o Template</h3>
              
              {carregandoTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : templates.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800">Nenhum template disponível. Crie templates em <strong>Integração → Templates</strong>.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map(template => (
                    <div
                      key={template.name}
                      onClick={() => setTemplateSelecionado(template)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        templateSelecionado?.name === template.name
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{template.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {template.body || template.components?.find((c: any) => c.type === 'BODY')?.text || 'Sem descrição'}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              template.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                              template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {template.status}
                            </span>
                            <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                              {template.category}
                            </span>
                          </div>
                        </div>
                        {templateSelecionado?.name === template.name && (
                          <CheckCircle className="h-5 w-5 text-blue-600 ml-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ETAPA 2: Seleção de Filtro e Destinatários */}
          {currentStep === 'filtro' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione os Destinatários</h3>
              
              {/* Seleção de Tipo de Filtro */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => {
                    setFilterType('inativos');
                    loadDestinatarios('inativos');
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    filterType === 'inativos'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900">Clientes Inativos</h4>
                  <p className="text-sm text-gray-600 mt-1">Alunos marcados como inativos</p>
                </button>
                
                <button
                  onClick={() => {
                    setFilterType('atraso');
                    loadDestinatarios('atraso');
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    filterType === 'atraso'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900">Clientes em Atraso</h4>
                  <p className="text-sm text-gray-600 mt-1">Mensalidades vencidas</p>
                  {filterType === 'atraso' && (
                    <div className="mt-2">
                      <label className="text-xs text-gray-600">Dias de atraso mínimo:</label>
                      <input
                        type="number"
                        value={diasAtraso}
                        onChange={(e) => {
                          setDiasAtraso(parseInt(e.target.value) || 1);
                          loadDestinatarios('atraso');
                        }}
                        min="1"
                        className="input w-full mt-1"
                      />
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setFilterType('manual');
                    loadDestinatarios('manual');
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    filterType === 'manual'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900">Seleção Manual</h4>
                  <p className="text-sm text-gray-600 mt-1">Escolha destinatários individualmente</p>
                </button>
              </div>

              {/* Lista de Destinatários */}
              {carregandoDestinatarios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : destinatarios.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-gray-600">Nenhum destinatário encontrado com os filtros selecionados.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      {destinatariosSelecionados.size} de {destinatarios.length} selecionados
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const allIds = new Set(destinatarios.map(d => d.alunoId));
                          setDestinatariosSelecionados(allIds);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Selecionar Todos
                      </button>
                      <button
                        onClick={() => setDestinatariosSelecionados(new Set())}
                        className="text-sm text-gray-600 hover:text-gray-700"
                      >
                        Desmarcar Todos
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    {destinatarios.map(destinatario => (
                      <div
                        key={destinatario.alunoId}
                        className="p-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3"
                      >
                        <input
                          type="checkbox"
                          checked={destinatariosSelecionados.has(destinatario.alunoId)}
                          onChange={(e) => {
                            const newSelected = new Set(destinatariosSelecionados);
                            if (e.target.checked) {
                              newSelected.add(destinatario.alunoId);
                            } else {
                              newSelected.delete(destinatario.alunoId);
                            }
                            setDestinatariosSelecionados(newSelected);
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{destinatario.alunoNome}</p>
                          <p className="text-sm text-gray-600">{destinatario.alunoTelefone}</p>
                          {destinatario.diasAtraso && (
                            <p className="text-xs text-orange-600 mt-1">
                              {destinatario.diasAtraso} dia(s) de atraso
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ETAPA 3: Preview */}
          {currentStep === 'preview' && preview && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview da Mensagem</h3>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">Para:</p>
                  <p className="font-medium text-gray-900">{preview.destinatario.alunoNome}</p>
                  <p className="text-sm text-gray-600">{preview.destinatario.alunoTelefone}</p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4 whitespace-pre-wrap">
                  {preview.mensagemPreview}
                </div>
                
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    Total de destinatários: <strong>{destinatariosSelecionados.size}</strong>
                  </span>
                  <span className="text-gray-600">
                    Custo estimado: <strong className="text-green-600">R$ {custoTotal.toFixed(2)}</strong>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ETAPA 4: Confirmação */}
          {currentStep === 'confirmacao' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmação de Envio</h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Template:</span>
                    <span className="font-semibold text-gray-900">{templateSelecionado?.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Destinatários:</span>
                    <span className="font-semibold text-gray-900">{destinatariosSelecionados.size}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Custo Total Estimado:</span>
                    <span className="font-semibold text-green-600 text-lg">R$ {custoTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ETAPA 5: Acompanhamento */}
          {currentStep === 'acompanhamento' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acompanhamento do Disparo</h3>
              
              {/* Barra de Progresso */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progresso</span>
                  <span className="text-sm text-gray-600">
                    {progresso.enviados} / {progresso.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${(progresso.enviados / progresso.total) * 100}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-gray-600">Enviados: {progresso.enviados}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-600">Entregues: {progresso.entregues}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-purple-600" />
                    <span className="text-gray-600">Lidos: {progresso.lidos}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-gray-600">Falhas: {progresso.falhas}</span>
                  </div>
                </div>
              </div>

              {/* Lista de Status */}
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {Array.from(disparoStatus.values()).map(status => (
                  <div
                    key={status.alunoId}
                    className="p-3 border-b border-gray-100 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{status.alunoNome}</p>
                      <p className="text-sm text-gray-600">{status.alunoTelefone}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {status.status === 'pending' && <Clock className="h-5 w-5 text-gray-400" />}
                      {status.status === 'sending' && <Loader className="h-5 w-5 text-blue-600 animate-spin" />}
                      {status.status === 'sent' && <CheckCircle className="h-5 w-5 text-blue-600" />}
                      {status.status === 'delivered' && <Eye className="h-5 w-5 text-green-600" />}
                      {status.status === 'read' && <EyeOff className="h-5 w-5 text-purple-600" />}
                      {status.status === 'failed' && <XCircle className="h-5 w-5 text-red-600" />}
                      <span className={`text-sm font-medium ${
                        status.status === 'failed' ? 'text-red-600' :
                        status.status === 'read' ? 'text-purple-600' :
                        status.status === 'delivered' ? 'text-green-600' :
                        status.status === 'sent' ? 'text-blue-600' :
                        'text-gray-600'
                      }`}>
                        {status.status === 'pending' ? 'Pendente' :
                         status.status === 'sending' ? 'Enviando' :
                         status.status === 'sent' ? 'Enviado' :
                         status.status === 'delivered' ? 'Entregue' :
                         status.status === 'read' ? 'Lido' :
                         'Falhou'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botões de Navegação */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <button
              onClick={handlePreviousStep}
              disabled={currentStep === 'template' || currentStep === 'acompanhamento'}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            
            {currentStep !== 'acompanhamento' && (
              <button
                onClick={handleNextStep}
                disabled={
                  (currentStep === 'template' && !templateSelecionado) ||
                  (currentStep === 'filtro' && destinatariosSelecionados.size === 0) ||
                  (currentStep === 'preview' && !preview) ||
                  enviando
                }
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === 'confirmacao' ? 'Confirmar e Enviar' : 'Próximo'}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <NotificationComponent />
    </div>
  );
};

export default DisparoMensagens;

